import { Outlet, useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { BookOpen, HelpCircle, MessageSquare } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSchedulingTerminology } from '@/hooks/useSchedulingTerminology';
import { useUnreadMessageCount } from '@/hooks/useUnreadMessageCount';
import { MobileNav } from '@/components/MobileNav';
import { DesktopDrawer } from '@/components/DesktopDrawer';
import type { NavGroup } from '@/config/navigation';
import Footer from '@/components/Footer';
import { Header } from '@/components/Header';

/**
 * TeacherLayout Component
 *
 * Layout wrapper for teacher interface with teacher-specific navigation.
 * Provides a simplified, focused navigation experience for teaching staff.
 *
 * Features:
 * - Mobile-first responsive design
 * - Hamburger menu for mobile (<768px)
 * - Horizontal navigation for desktop (≥768px)
 * - iOS safe area support (notch + home indicator)
 * - Teacher-specific navigation items (My Blocks, Help, Settings)
 *
 * Architecture:
 * - Separate from admin Layout component (no shared code)
 * - Uses React Router Outlet pattern for nested routes
 * - Matches existing Layout.tsx patterns for consistency
 */
const TeacherLayout = () => {
  const { user, logout, tenant, viewMode, setViewMode } = useAuthStore();
  const navigate = useNavigate();
  const { isMobile } = useMediaQuery();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Get dynamic terminology based on school's scheduling mode
  const schedulingMode = tenant?.settings?.schedulingUiMode || 'both';
  const terminology = useSchedulingTerminology(schedulingMode);
  const counts = useUnreadMessageCount();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  /**
   * Teacher navigation groups for mobile menu
   *
   * Simplified navigation focused on teacher workflows:
   * - Dashboard: View daily blocks/periods and schedules
   * - Messages: Inbox (with unread badge) and Sent
   * - Help: Access support documentation
   *
   * Note: Routes are relative to teacher route tree (/, /help)
   * NOT /teacher/* because teachers get their own route tree in App.tsx
   *
   * Uses useMemo to prevent recreation on every render
   */
  const teacherNavGroups: NavGroup[] = useMemo(
    () => [
      {
        title: 'Dashboard',
        icon: BookOpen,
        links: [
          { to: '/', label: terminology.myBlocksLabel },
          { to: '/my-students', label: 'My Students' },
        ],
      },
      {
        title: 'Messages',
        icon: MessageSquare,
        requiresModule: 'messaging',
        badge: counts.total > 0 ? counts.total : undefined,
        links: [
          { to: '/messages?tab=inbox', label: 'Inbox', requiresModule: 'messaging', badge: counts.messageUnread },
          { to: '/messages?tab=updates', label: 'Updates', requiresModule: 'messaging', badge: counts.announcementUnread },
          { to: '/messages?tab=sent', label: 'Sent', requiresModule: 'messaging' },
        ],
      },
      {
        title: 'Help',
        icon: HelpCircle,
        links: [{ to: '/help', label: 'Help & Support' }],
      },
    ],
    [terminology.myBlocksLabel, counts]
  );

  // Outer container: column with header at top, then content row below
  const outerLayoutStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  };

  // Content row: drawer + main content side by side (below header)
  const contentRowStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
  };

  // Main content wrapper: column with main + footer
  const mainContentStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0, // Prevent flex item from overflowing
  };

  const mainStyle: React.CSSProperties = {
    flex: 1,
    padding: '1.5rem 0',
    // iOS safe area support (home indicator)
    paddingBottom: `calc(1.5rem + var(--safe-area-inset-bottom))`,
  };

  return (
    <div style={outerLayoutStyle}>
      {/* Header spans full width at top */}
      <Header
        tenant={tenant}
        user={user}
        onLogout={handleLogout}
        showHamburger={isMobile}
        onHamburgerClick={() => setMobileMenuOpen(true)}
      />

      {/* Mobile Navigation */}
      {isMobile && (
        <MobileNav
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          groups={teacherNavGroups}
          userInfo={
            user
              ? {
                  email: user.email,
                  role: user.role,
                }
              : undefined
          }
          viewMode={viewMode}
          onViewModeSwitch={(mode) => {
            setViewMode(mode);
            navigate('/');
          }}
          onLogout={handleLogout}
        />
      )}

      {/* Content row: drawer + main */}
      <div style={contentRowStyle}>
        {/* Desktop Navigation Drawer */}
        {!isMobile && <DesktopDrawer navGroups={teacherNavGroups} user={user} onLogout={handleLogout} />}

        {/* Main Content Area */}
        <div style={mainContentStyle}>
          {/* Main content area - React Router Outlet */}
          <main style={mainStyle}>
            <Outlet />
          </main>

          {/* Footer with safe area support */}
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default TeacherLayout;
